import { useAccountContext } from '@seek/hirer-graphql-react';
import { Hidden } from 'braid-design-system';
import React from 'react';

import {
  type JobActionMenuOption,
  useJobActionMenuOptions,
} from 'src/App/components/JobActions/useJobActionMenuOptions';
import type { JobListItemFragment } from 'src/graphql/__generated__';

import { useConfig } from '../../../hooks/useConfig';
import { useTracking } from '../../../hooks/useTracking';
import { redirectTo, openInNewTab } from '../../../utils/url';
import { ExpireJobDialog } from '../ExpireJobDialog/ExpireJobDialog';
import { JobInformationDialog } from '../JobInformationDialog/JobInformationDialog';

import { filterJobActionMenuOptions } from './FilterJobActionMenuOptions';
import { JobActionMenuDesktop } from './JobActionMenuDesktop/JobActionMenuDesktop';
import { JobActionMobile } from './JobActionMobile/JobActionMobile';
import { NotificationPreferenceDialog } from './NotificationPreferenceDialog';
import { ShareJobDialog } from './ShareJobDialog';
import { TeamAccessDialog } from './TeamAccess';
import type { JobActionStatus } from './jobActionStatus';
import { createJobAdPath, createMultiLocationPath } from './links';
import { JobAction } from './type';

export interface JobActionMenuProps {
  disabled?: boolean;
  job: JobListItemFragment;
  jobActionStatus: JobActionStatus;
}

interface JobActionState {
  isShareDialogOpen: boolean;
  isExpireJobDialogOpen: boolean;
  isJobInformationDialogOpen: boolean;
  isNotificationsDialogOpen: boolean;
  isTeamAccessDialogOpen: boolean;
}

const getMultiLocationFeatureAvailability = (
  multiLocationOption: JobActionMenuOption | undefined,
): 'hidden' | 'not available' | 'available' => {
  if (typeof multiLocationOption === 'undefined') {
    return 'hidden';
  }
  return multiLocationOption.inactive ? 'not available' : 'available';
};

export const JobActionMenu: React.FC<
  React.PropsWithChildren<JobActionMenuProps>
> = ({ disabled = false, job, jobActionStatus }) => {
  const { accountContext } = useAccountContext();
  const userId = accountContext?.currentUser?.id;
  const { site, urlResolver } = useConfig();
  const { trackEvent } = useTracking();

  const [state, setState] = React.useState<JobActionState>({
    isShareDialogOpen: false,
    isExpireJobDialogOpen: false,
    isJobInformationDialogOpen: false,
    isNotificationsDialogOpen: false,
    isTeamAccessDialogOpen: false,
  });

  const jobActionMenuOptions = filterJobActionMenuOptions(
    useJobActionMenuOptions(jobActionStatus),
    {
      jobLocationCountry: job.locations?.[0]?.country,
      siteName: site,
    },
  );

  const jobAdUrl = urlResolver({
    path: createJobAdPath(job, 'hirer-jobs-list'),
    product: 'candidate',
  });

  const seekAdvertiserId = accountContext?.currentAdvertiser?.seekId ?? '';

  const multiLocationUrl = urlResolver({
    path: createMultiLocationPath(job, seekAdvertiserId),
  });

  const onJobAction = (jobAction: JobAction) => {
    trackEvent('job_action_pressed', {
      jobId: job.id,
      linkAction: jobAction,
    });

    switch (jobAction) {
      case JobAction.EXPIRE:
        setState((prevState) => ({
          ...prevState,
          isExpireJobDialogOpen: true,
        }));
        break;
      case JobAction.VIEW:
        openInNewTab(jobAdUrl);
        break;
      case JobAction.MULTI_LOCATION:
        redirectTo(multiLocationUrl);
        break;
      case JobAction.VIEW_JOB_INFO:
        setState((prevState) => ({
          ...prevState,
          isJobInformationDialogOpen: true,
        }));
        break;
      case JobAction.NOTIFICATION_PREFERENCE:
        setState((prevState) => ({
          ...prevState,
          isNotificationsDialogOpen: true,
        }));
        break;
      case JobAction.TEAM_MEMBER_ACCESS:
        setState((prevState) => ({
          ...prevState,
          isTeamAccessDialogOpen: true,
        }));
        break;
      case JobAction.SHARE:
        setState((prevState) => ({
          ...prevState,
          isShareDialogOpen: true,
        }));
        break;
    }
  };

  const multiLocationOption = jobActionMenuOptions.find(
    (q) => q.value === JobAction.MULTI_LOCATION,
  );
  const multiLocationFeatureAvailability =
    getMultiLocationFeatureAvailability(multiLocationOption);
  const eventAttributesProps = {
    adStatus: job.status.toLowerCase(),
    isLoggedIn: true,
    jobId: job.id || '',
    multiLocationFeatureAvailability,
  };
  return (
    <>
      <Hidden below="tablet">
        <JobActionMenuDesktop
          disabled={disabled}
          eventAttributesProps={eventAttributesProps}
          id={`dropdown-menu-${job.id}`}
          jobActionMenuOptions={jobActionMenuOptions}
          onAction={onJobAction}
        />
      </Hidden>
      <Hidden above="mobile">
        <JobActionMobile
          disabled={disabled}
          eventAttributesProps={eventAttributesProps}
          id={`${job.id}`}
          jobActionMenuOptions={jobActionMenuOptions}
          onAction={onJobAction}
        />
      </Hidden>
      <ShareJobDialog
        job={job}
        open={state.isShareDialogOpen}
        onClose={() =>
          setState((prevState) => ({ ...prevState, isShareDialogOpen: false }))
        }
      />
      <ExpireJobDialog
        seekAdvertiserId={seekAdvertiserId}
        jobId={`${job.id}`}
        isCsView={
          accountContext?.headerFooterContext?.identityContext?.act?.isCsView ??
          false
        }
        open={state.isExpireJobDialogOpen}
        onClose={() =>
          setState((prevState) => ({
            ...prevState,
            isExpireJobDialogOpen: false,
          }))
        }
      />
      <JobInformationDialog
        jobInformation={{
          jobId: `${job.id}`,
        }}
        open={state.isJobInformationDialogOpen}
        onClose={() =>
          setState((prevState) => ({
            ...prevState,
            isJobInformationDialogOpen: false,
          }))
        }
      />
      <NotificationPreferenceDialog
        seekAdvertiserId={`${seekAdvertiserId}`}
        userId={`${userId}`}
        email={accountContext?.currentUser?.email ?? ''}
        job={job}
        open={state.isNotificationsDialogOpen}
        onClose={() =>
          setState((prevState) => ({
            ...prevState,
            isNotificationsDialogOpen: false,
          }))
        }
      />
      <TeamAccessDialog
        seekAdvertiserId={`${seekAdvertiserId}`}
        userId={`${userId}`}
        job={job}
        open={state.isTeamAccessDialogOpen}
        onClose={() =>
          setState((prevState) => ({
            ...prevState,
            isTeamAccessDialogOpen: false,
          }))
        }
      />
    </>
  );
};
